import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Form, Select } from 'antd';
import type { FormItemProps } from 'antd';
import { listAssignableSalesTeams } from '@/services/salesTeam.service';

type SalesTeamSelectProps = {
  name?: string;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  formItemProps?: FormItemProps;
};

export const SalesTeamSelect: React.FC<SalesTeamSelectProps> = ({
  name = 'sales_team_id',
  label = 'Sales team',
  required = true,
  disabled = false,
  formItemProps,
}) => {
  const { data: teams = [], isLoading, isError } = useQuery({
    queryKey: ['sales-teams-assignable'],
    queryFn: listAssignableSalesTeams,
  });
  const options = useMemo(
    () => teams.map((t) => ({ value: t.id, label: t.team_name })),
    [teams]
  );

  return (
    <Form.Item
      name={name}
      label={label}
      rules={required ? [{ required: true, message: 'Required' }] : undefined}
      extra={
        isError
          ? 'Could not load sales teams (missing permission or no team membership).'
          : !isLoading && options.length === 0
            ? 'No sales teams available — ask admin to add you to a sales team.'
            : undefined
      }
      {...formItemProps}
    >
      <Select
        showSearch
        optionFilterProp="label"
        loading={isLoading}
        disabled={disabled}
        placeholder="Select sales team"
        options={options}
        notFoundContent={isError ? 'Unable to load teams' : 'No teams'}
      />
    </Form.Item>
  );
};
